<template>
  <div class="modal">
    <div class="modal-content">
      <div class="modal-header">
        <h1 class="modal-title">Account details</h1>
        <button type="button" class="btn-close" data-bs-dismiss="modal" @click="$emit('close')"
                aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveDetails">
          <div class="mb-3">
            <label for="first_name" class="form-label">First name</label>
            <input type="text" class="form-control" v-model="first_name" id="first_name" required
                   ref="firstNameInput">
            <div v-if="firstNameError" class="text-danger">{{ firstNameError }}</div>
          </div>
          <div class="mb-3">
            <label for="last_name" class="form-label">Last name</label>
            <input type="text" class="form-control" v-model="last_name" id="last_name" required>
            <div v-if="lastNameError" class="text-danger">{{ lastNameError }}</div>
          </div>
          <div class="mb-3">
            <label for="mobile_number" class="form-label">Mobile number</label>
            <input type="tel" class="form-control" v-model="mobile_number" id="mobile_number" required>
            <div v-if="mobileNumberError" class="text-danger">{{ mobileNumberError }}</div>
            <div v-if="mobileNumberSuccess" class="text-success">{{ mobileNumberSuccess }}</div>
          </div>
          <div class="mb-3">
            <label for="favourite_co_workers" class="form-label">Favourite co-workers</label>
            <textarea type="text" class="form-control" v-model="favourite_co_workers" id="favourite_co_workers" rows="3"
                      maxlength="250"/>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="$emit('close')">Cancel</button>
        <button type="button" class="btn btn-primary" @click="saveDetails"
                :disabled="!firstNameValid || !lastNameValid || !mobileNumberValid">Save changes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {ref, watch} from "vue";
import {getCSRFToken} from "@/store/auth";
import {API_BASE_URL} from "@/config";

function debounce(fn, delay) {
  let timeoutID;
  return function (...args) {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => fn.apply(this, args), delay);
  };
}

async function callValidateMobileNumberAPI(mobileNumber) {
  try {
    const response = await fetch(`${API_BASE_URL}/validate-mobile-number`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken()
      },
      body: JSON.stringify({mobile_number: mobileNumber}),
      credentials: 'include'
    });
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('Error validating mobile number', err);
    return {status: 'invalid', errors: ['An error occurred. Please try again.']};
  }
}


export default {
  name: 'EditDetailsModal',
  props: {
    user: {
      type: Object,
      required: true,
    }
  },

  setup(props) {
    const first_name = ref(props.user?.first_name || '');
    const firstNameError = ref('')
    const firstNameValid = ref(false)
    const last_name = ref(props.user?.last_name || '');
    const lastNameError = ref('')
    const lastNameValid = ref(false)
    const mobile_number = ref(props.user?.mobile_number || '');
    const mobileNumberError = ref('')
    const mobileNumberSuccess = ref('')
    const mobileNumberValid = ref(false)
    const favourite_co_workers = ref(props.user?.favourite_co_workers || '')

    const validateFirstName = debounce(async (newFirstName) => {
      if (newFirstName === '') {
        firstNameError.value = 'Enter your first name'
        firstNameValid.value = false
      } else {
        firstNameError.value = ''
        firstNameValid.value = true
      }
    }, 100);

    const validateLastName = debounce(async (newLastName) => {
      if (newLastName === '') {
        lastNameError.value = 'Enter your last name'
        lastNameValid.value = false
      } else {
        lastNameError.value = ''
        lastNameValid.value = true
      }
    }, 100);


    watch(first_name, (newFirstName) => {
      validateFirstName(newFirstName);
    })

    watch(last_name, (newLastName) => {
      validateLastName(newLastName);
    })

    const validateMobileNumber = debounce(async (newMobileNumber) => {
      if (newMobileNumber) {
        const result = await callValidateMobileNumberAPI(newMobileNumber)
        if (result.status === 'valid') {
          // Good mobile number
          mobileNumberSuccess.value = result.success
          mobileNumberError.value = ''
          mobileNumberValid.value = true
        } else {
          // Bad mobile number
          mobileNumberSuccess.value = ''
          mobileNumberError.value = result.errors.join(', ')
          mobileNumberValid.value = false
        }
      } else {
        mobileNumberError.value = ''
        mobileNumberValid.value = false
      }
    }, 250);
    watch(mobile_number, (newMobileNumber) => {
      validateMobileNumber(newMobileNumber);
    })

    // Initial validation when the modal is displayed
    // Launched to set the mobileNumberValid value,
    // so that the "Save changes" button is disabled if the mobile number is invalid
    validateFirstName(first_name.value);
    validateLastName(last_name.value);
    validateMobileNumber(mobile_number.value);

    return {
      first_name,
      firstNameError,
      firstNameValid,
      last_name,
      lastNameError,
      lastNameValid,
      mobile_number,
      mobileNumberError,
      mobileNumberSuccess,
      mobileNumberValid,
      favourite_co_workers,
    }
  },

  mounted() {
    this.$refs.firstNameInput.focus();
  },
  data() {
    return {
      formData: {...this.user},
    };
  },
  methods: {
    async saveDetails() {
      this.formData.first_name = this.first_name;
      this.formData.last_name = this.last_name;
      this.formData.mobile_number = this.mobile_number;
      this.formData.favourite_co_workers = this.favourite_co_workers;
      this.$emit('save-details', this.formData);
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
  position: fixed;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.4);
}

.modal-content {
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 80%;
}

.close {
  color: #aaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
  cursor: pointer;
}
</style>